import React from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {Formik, Form} from 'formik';
import {useDispatch, useSelector} from 'react-redux';

import AdminFeatureFlagService, {AdminFeatureFlag} from 'src/services/admin-feature-flag';
import {flashMessage, errorMessage} from 'shared/actions/notifications';
import {addOrEditFeatureFlag, deleteFeatureFlag} from 'src/redux/admin/actions/feature-flags';
import {AdminStore} from 'src/types/store';

import Modal from 'shared/components/modal';
import Label from 'shared/components/label';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import FormikTextArea from 'src/components/formik-text-area';

import styles from './feature-flag-modal.module.scss';

export type FeatureFlagForm = Omit<
  AdminFeatureFlag,
  'id' | 'history' | 'options' | 'usersListDisplay' | 'users' | 'groups'
> &
  Partial<Pick<AdminFeatureFlag, 'id'>> & {
    users: string;
    groups: string;
  };

const FeatureFlagModal = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {pathname} = useLocation();
  const {id} = useParams<{id: string}>();
  const isNew = id === 'new';
  const feature = useSelector((state: AdminStore) => (isNew ? null : state.featureFlags[id]));

  const featureFlags = useSelector((state: AdminStore) => state.featureFlags);

  const handleClose = () =>
    history.replace({
      pathname: pathname.substring(0, pathname.lastIndexOf('/')),
      state: {skipScrolling: true},
    });

  return (
    <Modal show onClose={handleClose} forceNotIonic>
      <Modal.TextHeader onClose={handleClose}>
        {isNew ? 'Add new feature flag' : 'Edit feature flag'}
      </Modal.TextHeader>
      <Formik<FeatureFlagForm>
        initialValues={
          !isNew && feature
            ? {
                id: feature.id,
                name: feature.name,
                description: feature.description,
                percentage: feature.percentage,
                users: feature.users.join(','),
                groups: feature.groups.join(','),
              }
            : {
                name: '',
                description: '',
                percentage: 0,
                users: '',
                groups: '',
              }
        }
        onSubmit={async (values, {setErrors}) => {
          try {
            const response = await AdminFeatureFlagService.createOrUpdate(values);
            dispatch(addOrEditFeatureFlag(response));
            handleClose();
            dispatch(flashMessage('Saved feature flag!'));
          } catch (e: unknown) {
            const validationErrors = (e as Record<string, unknown>)?.errors;
            if (Array.isArray(validationErrors)) {
              const fieldErrors: Record<string, string> = {};
              for (const err of validationErrors) {
                if (err.field && err.message) {
                  fieldErrors[err.field] = err.message;
                }
              }
              if (Object.keys(fieldErrors).length) {
                setErrors(fieldErrors);
                return;
              }
            }
            dispatch(errorMessage());
          }
        }}
        validate={(values) => {
          const errors: Partial<FeatureFlagForm> = {};
          if (isNew && values.name && featureFlags[values.name]) {
            errors.name = 'A feature flag with this name already exists';
          }
          return errors;
        }}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <Modal.Body>
              <Label required noTopPadding>
                Name
                <FormikTextInput name="name" disabled={!isNew} required />
              </Label>
              <Label required>
                Description
                <FormikTextInput name="description" required />
              </Label>
              <Label>
                Percentage
                <FormikTextInput name="percentage" type="number" />
              </Label>
              <Label>
                Users
                <FormikTextArea name="users" />
              </Label>
              {!!feature?.usersListDisplay.length && (
                <pre className="mt-12">{feature.usersListDisplay.join('\n')}</pre>
              )}
              <Label>
                Groups
                <FormikTextArea name="groups" />
              </Label>

              {!!feature?.history.length && (
                <>
                  <div className="shared-font body bold mb-12 mt-20">History</div>
                  <pre className={styles.history}>
                    {feature.history
                      .toReversed()
                      .map(
                        (edit, i) =>
                          `${JSON.stringify(edit)}\n${i < feature.history.length - 1 ? '\n' : ''}`
                      )}
                  </pre>
                </>
              )}
            </Modal.Body>
            <Modal.Footer
              primaryButton={{
                type: 'submit',
                width: 120,
                disabled: formik.isSubmitting || !formik.isValid,
                text: formik.isSubmitting ? 'Saving...' : 'Save',
              }}
              tertiaryButton={
                !isNew && feature
                  ? {
                      text: 'Delete',
                      color: 'red',
                      onClick: async () => {
                        try {
                          await AdminFeatureFlagService.destroy(feature.id);
                          dispatch(deleteFeatureFlag(feature.id));
                          handleClose();
                          dispatch(flashMessage('Deleted feature flag!'));
                        } catch {
                          dispatch(errorMessage());
                        }
                      },
                    }
                  : undefined
              }
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default FeatureFlagModal;
